export enum AppMode {
  TECH_ASSISTANT = "tech_assistant",
  BLOG_ENGINE = "blog_engine",
  PERSONAL_VAULT = "personal_vault",
  INVENTORY_SCOUT = "inventory_scout",
  LEAD_RESPONDER = "lead_responder",
  SEO_LISTINGS = "seo_listings",
  WEB_CREATIVE = "web_creative",
  FRONT_DESK = "front_desk",
}

export type ThemeMode = "rodgerspc" | "light" | "dark";
export type ChatThemeMode = "light" | "dark";

export type IndexingStatus = "idle" | "loading" | "ready" | "partial" | "failed";

export type AttachmentType =
  | "image"
  | "document"
  | "pdf"
  | "spreadsheet"
  | "text"
  | "html"
  | "markdown"
  | "json"
  | "csv"
  | "binary"
  | string;

export type GeneratedImageAssetSource = "generated_image" | "drive" | "upload" | "export" | string;

export interface GeneratedImageAsset {
  id: string;
  name: string;
  mimeType: string;
  width?: number;
  height?: number;
  sizeBytes?: number;

  /**
   * Stable rendered image data. Keep this as a data URL or retrievable URL so
   * chat reloads and exports can render the image until the chat is cleared.
   */
  dataUrl?: string;
  url?: string;
  storagePath?: string;

  source: GeneratedImageAssetSource;
  prompt?: string;
  createdAt: string;
}

export type CommandType =
  | "powershell_single"
  | "powershell_batch"
  | "cmd_step"
  | "cmd_batch"
  | "shell_step"
  | "bash_step"
  | "bash_batch"
  | "registry_step"
  | string;

export type AccuracyMode = "strict" | "balanced" | "creative";
export type LegacyResponseLength = "quick" | "normal" | "detailed";
export type OutputLength = "tight" | "short" | "normal" | "long" | "full";
export type ResponseLength = LegacyResponseLength | OutputLength;
export type JobSensitivity = "routine" | "important" | "critical";
export type AiPreset = "bench_job" | "front_counter" | "creative_seo" | "custom" | string;
export type AiVerbosity = "minimum" | "medium" | "high";

export type ModelRoutingMode = "rodgerspc_auto" | "manual" | "default" | string;

export type ModelRoutingSignal =
  | "current_public_data"
  | "drive_record_lookup"
  | "high_risk_bench"
  | "board_level"
  | "data_recovery"
  | "customer_sensitive"
  | "pricing_sourcing"
  | "seo_content"
  | "simple_reply"
  | "image_generation"
  | string;

export interface ModelRoutingHint {
  mode: AppMode | string;
  modelRoutingMode?: ModelRoutingMode;
  fastModeEnabled?: boolean;
  accuracyMode?: AccuracyMode | string;
  responseLength?: ResponseLength | string;
  jobSensitivity?: JobSensitivity | string;
  aiPreset?: AiPreset;
  driveSyncEnabled?: boolean;
  searchGroundingEnabled?: boolean;
  imageGenerationEnabled?: boolean;
  promptSignals?: ModelRoutingSignal[];
  promptLength?: number;
  requestedAt?: string;
}

export interface ModelRoutingTelemetry {
  mode?: AppMode | string;
  requestClass?: string;
  requestStage?: string;
  modelRoutingMode?: ModelRoutingMode;
  primaryModel?: string;
  fallbackModel?: string;
  emergencyModel?: string;
  selectedModel?: string;
  usedFallback?: boolean;
  routeReason?: string;
  temperature?: number;
}

export type DriveSourceType =
  | "google_doc"
  | "google_sheet"
  | "google_slide"
  | "docx"
  | "doc"
  | "pdf"
  | "image"
  | "text"
  | "html"
  | "markdown"
  | "json"
  | "csv"
  | "spreadsheet"
  | "binary"
  | "unknown"
  | string;

export type DriveExtractionStatus =
  | "idle"
  | "loading"
  | "ready"
  | "partial"
  | "failed"
  | string;

export type ContextSource =
  | "source_of_truth"
  | "focused_drive"
  | "retrieved"
  | "upload"
  | "attachment"
  | "manual"
  | "web"
  | string;

export type EvidenceState =
  | "confirmed"
  | "confirmed_visible"
  | "suspected"
  | "unclear"
  | "needs_zoom"
  | "not_shown"
  | "missing"
  | "not_loaded"
  | "metadata_only"
  | "visual_payload"
  | "visual_payload_missing"
  | "placeholder_rejected"
  | "truncated_placeholder"
  | "unverified"
  | string;

export type BenchStage =
  | "intake"
  | "baseline"
  | "reproduce"
  | "confirm"
  | "fix"
  | "validate"
  | "price"
  | "document"
  | "customer_reply"
  | "general"
  | string;

export type RodgersPcRequestClass =
  | "live_bench"
  | "data_recovery"
  | "customer_communication"
  | "pricing_sourcing"
  | "seo_content"
  | "drive_record_lookup"
  | "export_document"
  | "general"
  | string;

export type VisualInspectionState =
  | "confirmed_visible"
  | "unclear_needs_zoom"
  | "not_shown"
  | "not_loaded"
  | "placeholder_rejected"
  | string;

export interface DriveExtractedImage {
  id?: string;
  name: string;
  mimeType: string;

  /**
   * Raw base64 only, no data:mime prefix.
   */
  base64?: string;

  /**
   * Some UI paths store full data URLs. Server code accepts either this or base64.
   */
  data?: string;

  type?: string;
  size?: number;
  width?: number;
  height?: number;
  pageNumber?: number;
  source?: "drive" | "attachment" | "pdf_page" | "docx_embedded" | "standalone_image" | string;
  caption?: string;
  path?: string;

  /**
   * Evidence boundary:
   * A filename/caption is metadata. A base64/data payload is visual evidence available to the model.
   */
  evidenceState?: EvidenceState;
  visualInspectionState?: VisualInspectionState;
  isRealPayload?: boolean;
  rejectedReason?: string;
}

export interface LiveBenchLedger {
  customerName?: string;
  device?: string;
  issue?: string;
  stage?: BenchStage;
  confirmedFacts?: string[];
  completedActions?: string[];
  removedItems?: string[];
  disabledItems?: string[];
  installedItems?: string[];
  changedSettings?: string[];
  testsRun?: string[];
  results?: string[];
  openQuestions?: string[];
  redFlags?: string[];
  unclearVisualItems?: string[];
  correctionsApplied?: string[];
  pendingNextSteps?: string[];
  lastUpdatedAt?: string;
}

export interface CorrectionSignal {
  wrongClaim?: string;
  correctedFact: string;
  source?: "joshua" | "drive" | "web" | "visual" | string;
  appliedAt?: string;
}

export interface DriveLoadedContent {
  fileId: string;
  name: string;
  path?: string;
  size?: number;
  mimeType?: string;
  sourceType: DriveSourceType;
  content: string;
  images?: DriveExtractedImage[];
  extractionStatus: DriveExtractionStatus;
  extractionWarnings?: string[];
  webViewLink?: string;
  modifiedTime?: string;
}

/**
 * Drive context model:
 *
 * Simple rule:
 * - checked / focused / gold = usable RodgersPC Drive knowledge
 * - unchecked = ignored
 *
 * Folders and files use the same rule.
 * A checked folder means anything inside it is allowed to be searched/used when relevant.
 * A checked file means that file is allowed to be used when relevant.
 *
 * Do not dump whole folders into the prompt.
 * Retrieve relevant Drive content on demand.
 */
export interface WorkspaceItem {
  id: string;
  name: string;
  type: "file" | "folder";
  path: string;

  /**
   * Google Drive metadata.
   */
  parentId?: string;
  mimeType?: string;
  modifiedTime?: string;
  webViewLink?: string;
  iconLink?: string;
  shortcutDetails?: {
    targetId?: string;
    targetMimeType?: string;
  };

  /**
   * Drive focus state.
   *
   * checked:
   * - true means focused/gold/usable
   * - false or undefined means ignored
   *
   * sourceOfTruth:
   * - true means this belongs to the canonical Source of Truth set
   * - Source of Truth should be focused by default
   */
  checked?: boolean;
  sourceOfTruth?: boolean;

  /**
   * Content should stay empty until fetched on demand.
   */
  content?: string;
  size?: number;

  /**
   * Loaded Drive payload fields.
   * These support DOCX/PDF/image extraction and stop the app from being text-only.
   */
  loadedContent?: DriveLoadedContent;
  extractedImages?: DriveExtractedImage[];
  sourceType?: DriveSourceType;
  extractionStatus?: DriveExtractionStatus;
  extractionWarnings?: string[];

  /**
   * Tree/UI state.
   */
  children?: WorkspaceItem[];
  indexingStatus?: IndexingStatus;
  hidden?: boolean;
  error?: string;
  expanded?: boolean;
  depth?: number;

  /**
   * Optional UI helper for partial parent checkbox state.
   * This should be calculated from children, not treated as source data.
   */
  indeterminate?: boolean;
}

export interface DriveContextStats {
  treeItems: number;
  focusedItems: number;
  focusedFolders: number;
  focusedFiles: number;
  sourceOfTruthItems: number;
  loadedFiles: number;
  loadedCharacters: number;
  visualPayloads?: number;
  failedExtractions?: number;
}

export interface DriveFocusMetadataEntry {
  id: string;
  name: string;
  path: string;
  type: "file" | "folder";
  size?: number;
  mimeType?: string;
  modifiedTime?: string;
  checked?: boolean;
  sourceOfTruth?: boolean;
  indexingStatus?: IndexingStatus;
  extractionStatus?: DriveExtractionStatus;
  sourceType?: DriveSourceType;
  evidenceState?: EvidenceState;
  visualInspectionState?: VisualInspectionState;
  isRealPayload?: boolean;
  rejectedReason?: string;
}



export interface ActiveContextPayload {
  path: string;
  name?: string;
  size?: number;
  content: string;
  source?: ContextSource;
  fileId?: string;
  mimeType?: string;
  sourceType?: DriveSourceType;
  images?: DriveExtractedImage[];
  extractionStatus?: DriveExtractionStatus;
  extractionWarnings?: string[];

  /**
   * Server/client evidence boundary helpers.
   * These fields are export/debug metadata only; they must not turn metadata into evidence.
   */
  evidenceStatus?:
    | "metadata_only"
    | "text_loaded"
    | "visual_payload_loaded"
    | "text_and_visual_payload"
    | "loaded_no_readable_content"
    | "load_failed"
    | string;
  hasReadableText?: boolean;
  visualPayloadCount?: number;
  canClaimVisualInspection?: boolean;
  retrievalEngine?: "immediate_request" | "background_drive_brain" | "unknown" | string;
  evidenceLoadedAt?: string;

  /**
   * Older compatibility fields.
   */
  loadedAt?: string;
  evidenceState?: EvidenceState;
  visualInspectionState?: VisualInspectionState;
  isRealPayload?: boolean;
  rejectedReason?: string;
}



export interface DriveEvidenceLoadedFile {
  fileId?: string;
  name?: string;
  path: string;
  source?: ContextSource | string;
  sourceType?: DriveSourceType;
  mimeType?: string;
  extractionStatus?: DriveExtractionStatus;
  extractionWarnings?: string[];
  evidenceStatus?: string;
  hasReadableText?: boolean;
  hasLoadedText?: boolean;
  textLoaded?: boolean;
  contentLoaded?: boolean;
  textChars?: number;
  contentChars?: number;
  visualPayloadCount?: number;
  visualPayloads?: number;
  visionPayloadsSent?: number;
  imageCount?: number;
  visualPayloadsSent?: boolean;
  visualSent?: "yes" | "no" | string;
  canClaimVisualInspection?: boolean;
  retrievalEngine?: "immediate_request" | "background_drive_brain" | "unknown" | string;
  evidenceLoadedAt?: string;
  images?: DriveExtractedImage[];
}

export interface DriveEvidenceFailedLoad {
  fileId?: string;
  name?: string;
  path?: string;
  engine?: "immediate" | "background" | "source_of_truth_preload" | string;
  error: string;
  reason?: string;
}

export interface DriveEvidenceSummary {
  driveSyncEnabled?: boolean;
  driveEnabled?: boolean;

  /** App/export count names. */
  loadedDriveFileCount?: number;
  loadedDriveTextFileCount?: number;
  loadedDriveVisualPayloadCount?: number;
  canClaimVisualInspectionFileCount?: number;
  loadedDriveFiles?: DriveEvidenceLoadedFile[];
  failedDriveLoadCount?: number;
  failedDriveLoads?: DriveEvidenceFailedLoad[];
  metadataOnlyFocusedFileCount?: number;

  /** Server runtime count names. */
  loadedFileCount?: number;
  loadedTextFileCount?: number;
  visualFileCount?: number;
  visualPayloadCount?: number;
  visualPayloadsSentCount?: number;
  driveVisualPayloadsSent?: number;
  files?: DriveEvidenceLoadedFile[];

  /** Older aliases supported by exporter. */
  loadedFiles?: number | DriveEvidenceLoadedFile[];
  loadedDriveFilesCount?: number;
  loadedTextFiles?: number;
  textFilesLoaded?: number;
  visualPayloadsSent?: number;
  inlineVisionPayloads?: number;
  failedFiles?: number;
  loadFailedFiles?: number;
  failedLoadCount?: number;
  loadFailures?: DriveEvidenceFailedLoad[];

  [key: string]: any;
}

export interface SitemapEntry {
  url: string;
  locs: string[];
  status: "pending" | "loaded" | "error" | "failed";
  error?: string;
}

export interface AttachedFile {
  id: string;
  name: string;
  type: AttachmentType;
  content: string;

  /**
   * base64 is used for images, PDFs, and binary uploads sent through Gemini inlineData.
   * content remains available for preview and text extraction.
   */
  base64?: string;

  rawSize?: number;
  mimeType?: string;
  lastModified?: number;
  source?: "upload" | "paste" | "drive" | "generated" | string;
  assetId?: string;
  imageAsset?: GeneratedImageAsset;

  /**
   * Payload hygiene: App/exporter should reject placeholder strings such as
   * "... [Payload size optimized]" as real visual evidence.
   */
  isRealPayload?: boolean;
  payloadRejected?: boolean;
  rejectedReason?: string;
  visualInspectionState?: VisualInspectionState;
}

export interface GeneratedCommand {
  id?: string;
  type: CommandType;
  title: string;
  code: string;

  /**
   * Optional command display metadata.
   */
  shell?: string;
  adminRequired?: boolean;
  restartRequired?: boolean;
  risky?: boolean;
  warning?: string;
  description?: string;
  scope?: string;
}

export interface ProjectReport {
  id?: string;
  title: string;
  text: string;
  originalText?: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;

  commands?: GeneratedCommand[];
  reports?: ProjectReport[];
  imageUrl?: string;
  imageAsset?: GeneratedImageAsset;
  imageAssets?: GeneratedImageAsset[];
  originalContent?: string;
  attachments?: AttachedFile[];

  /**
   * Mode active when this message was sent. Required for cross-mode handoff without
   * confusing which RodgersPC employee brain produced each turn.
   */
  modeAtSend?: AppMode;

  /**
   * Optional live bench state returned/updated by server.ts. UI can ignore safely.
   */
  liveBenchLedger?: LiveBenchLedger;
  correctionSignals?: CorrectionSignal[];
  requestClass?: RodgersPcRequestClass;
  requestStage?: BenchStage;

  /**
   * Retry/error state used by ChatWorkspace and App.tsx.
   * App can retry the closest previous user message without requiring Joshua to retype.
   */
  isError?: boolean;
  retryOfMessageId?: string;
  errorCode?: string;

  /**
   * Mode-change divider message.
   */
  isModeChangeIndicator?: boolean;
  newMode?: AppMode;
  prevMode?: AppMode;

  /**
   * Optional response metadata returned by server.ts.
   * UI can ignore this safely if unused.
   */
  mode?: AppMode;
  benchStage?: BenchStage;
  deterministicBenchCase?: string;
  deterministicBenchStage?: BenchStage;
  sourceSummary?: string;
  warningSummary?: string;

  /**
   * Evidence/export telemetry returned by server.ts and used by AIExporterModal.
   */
  driveEvidenceSummary?: DriveEvidenceSummary;
  clientDriveEvidenceSummary?: DriveEvidenceSummary;
  modelRoute?: {
    route?: string;
    primaryModel?: string;
    fallbackModel?: string;
    reason?: string;
    [key: string]: any;
  };
  webResearch?: {
    query?: string;
    resultCount?: number;
    readCount?: number;
    warnings?: string[];
    [key: string]: any;
  };
}

export interface LinkValidationResult {
  url: string;
  valid: boolean;
  statusCode?: number;
  message?: string;
}


export interface CustomerBenchDeviceSecurityState {
  enabled: boolean;
  persistence: "memory" | "local" | "session" | string;
  forceAccountChooser: boolean;
  forceConsent?: boolean;
  maxAgeZero?: boolean;
  clearedAppStorageAt?: string;
  warning?: string;
}

export interface WorkspaceStatePayload {
  userId: string;
  email: string;

  globalTheme: ThemeMode;
  chatTheme: ChatThemeMode;
  currentMode: AppMode;

  driveSyncEnabled: boolean;
  searchGroundingEnabled: boolean;
  imageGenerationEnabled: boolean;
  fastModeEnabled: boolean;

  driveSidebarVisible: boolean;
  bottomPanelVisible: boolean;

  aiTemperature: number;
  aiBehavior: AccuracyMode;
  aiVerbosity?: AiVerbosity;

  accuracyMode?: AccuracyMode | string;
  responseLength?: ResponseLength | string;
  jobSensitivity?: JobSensitivity | string;
  aiPreset?: AiPreset;

  messages: ChatMessage[];
  items: WorkspaceItem[];

  isCleared?: boolean;
  clearedAt?: string | null;
  activeChatId?: string;
  lastUpdated?: string;
}

export interface SavedChatSummary {
  id: string;
  title: string;
  subject?: string;
  customerName?: string;
  mode?: AppMode;
  messageCount: number;
  estimatedBytes: number;
  createdAt: string;
  updatedAt: string;
  archived?: boolean;
  pinned?: boolean;
  tags?: string[];

  /**
   * Compact memory summary lets the app keep useful continuity without saving giant raw chats forever.
   */
  memorySummary?: string;
}

export interface SavedChatPayload extends SavedChatSummary {
  messages: ChatMessage[];
  items?: WorkspaceItem[];
  settings?: {
    currentMode?: AppMode;
    accuracyMode?: AccuracyMode | string;
    responseLength?: ResponseLength | string;
    jobSensitivity?: JobSensitivity | string;
    aiPreset?: AiPreset;
    searchGroundingEnabled?: boolean;
    driveSyncEnabled?: boolean;
    imageGenerationEnabled?: boolean;
    fastModeEnabled?: boolean;
  };
}

export interface ServerChatRequest {
  messages: Array<{
    role: "user" | "assistant";
    content: string;
    imageUrl?: string;
    imageAsset?: GeneratedImageAsset;
    imageAssets?: GeneratedImageAsset[];
    attachments?: AttachedFile[];
    modeAtSend?: AppMode | string;
    isContinuityPacket?: boolean;
    isModeChangeIndicator?: boolean;
    newMode?: AppMode | string;
    prevMode?: AppMode | string;
  }>;
  activeContext?: ActiveContextPayload[];
  searchScopeMetadata?: string;
  mode: AppMode;
  searchGroundingEnabled: boolean;
  imageGenerationEnabled: boolean;
  driveSyncEnabled: boolean;
  fastModeEnabled: boolean;
  aiTemperature: number;
  aiBehavior?: AccuracyMode | string;
  aiVerbosity?: AiVerbosity | string;
  accuracyMode?: AccuracyMode | string;
  responseLength?: ResponseLength | string;
  jobSensitivity?: JobSensitivity | string;
  aiPreset?: AiPreset;

  /**
   * Server model selection stays automatic for now.
   * App.tsx sends these hints so server.ts can choose the best Gemini model by RodgersPC task/mode.
   */
  driveBrainUseMode?: "shop_memory_not_job_dump" | string;
  modelRoutingMode?: ModelRoutingMode;
  modelRoutingHint?: ModelRoutingHint;

  /**
   * Cross-mode same-job continuity packet built by App.tsx.
   * Server may use it as handoff context, but Joshua corrections and current message win.
   */
  crossModeContinuityPacket?: string;
  selectedResponseLength?: ResponseLength | string;

  /**
   * Client-side Drive evidence/failure summary from App.tsx. Server merges this with runtime visual-send status.
   */
  driveEvidenceSummary?: DriveEvidenceSummary | null;
}

export interface ServerChatResponse {
  text: string;
  commands?: GeneratedCommand[];
  reports?: ProjectReport[];
  imageUrl?: string;
  imageAsset?: GeneratedImageAsset;
  imageAssets?: GeneratedImageAsset[];
  mode?: AppMode;
  benchStage?: BenchStage;
  deterministicBenchCase?: string;
  deterministicBenchStage?: BenchStage;
  sourceSummary?: string;
  warningSummary?: string;

  /**
   * Optional backend metadata. UI can ignore safely. Useful for later model-control UI/debug.
   */
  modeIsBusinessCritical?: boolean;
  modelRouting?: ModelRoutingTelemetry;
  deterministicRoute?: {
    matched: boolean;
    reason?: string;
  };
  webResearch?: {
    query?: string;
    resultCount?: number;
    readCount?: number;
    warnings?: string[];
    [key: string]: any;
  };
  driveEvidenceSummary?: DriveEvidenceSummary;
  clientDriveEvidenceSummary?: DriveEvidenceSummary | null;
  modelRoute?: {
    route?: string;
    primaryModel?: string;
    fallbackModel?: string;
    reason?: string;
    [key: string]: any;
  };
  requestClass?: RodgersPcRequestClass;
  requestStage?: BenchStage;
  liveBenchLedger?: LiveBenchLedger;
  correctionSignals?: CorrectionSignal[];
}